import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { ToastrService } from 'ngx-toastr';
import { ContractType } from 'src/app/_enums/contract-type.enum';
import { DocumentType } from 'src/app/_enums/document-type.enum';
import { InsuranceFlowStatus } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { getInsuranceTypeLabel } from 'src/app/_enums/insurance-type.enum';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { InsuranceFlowService } from 'src/app/_services/insurance-flow.service';
import { APIResponse } from 'src/app/_types/api-responses.types';
import {
  DocumentSummary,
  SignedContractDocument,
} from 'src/app/_types/document.types';
import {
  CentralisationFlowResponse,
  CompanyMailRecipient,
  RenderCentralisationMailsResponse,
} from 'src/app/_types/insurance-flow.types';
import { richTextRequired } from 'src/app/Dashboard/customer/communications/compose/compose-validators';
import { DocumentPickerComponent } from 'src/app/Dashboard/customer/communications/compose/document-picker/document-picker.component';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { DocumentSummaryComboboxComponent } from 'src/app/shared/components/document-summary-combobox/document-summary-combobox.component';
import {
  FilterTab,
  FilterTabsComponent,
} from 'src/app/shared/components/filter-tabs/filter-tabs.component';
import { RequiredFieldMarkComponent } from 'src/app/shared/components/required-field-mark/required-field-mark.component';
import { SignedContractDocumentComboboxComponent } from 'src/app/shared/components/signed-contract-document-combobox/signed-contract-document-combobox.component';
import { EDITOR_PLUGINS, EDITOR_TOOLBAR } from 'src/app/shared/editor-config';
import { IconsModule } from 'src/app/shared/icons/icons.module';

import { TaskDetailsService } from '../../../../task-details.service';
import { BaseStepperAction } from '../../../task-history/stepper-actions/base-action';

export enum CentralisationSendMode {
  PLATFORM = 'PLATFORM',
  OFF_PLATFORM = 'OFF_PLATFORM',
}

@Component({
  selector: 'app-send-centralisation-action',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    IconsModule,
    ActionModalComponent,
    SignedContractDocumentComboboxComponent,
    DocumentSummaryComboboxComponent,
    RequiredFieldMarkComponent,
    FilterTabsComponent,
    EditorComponent,
    DocumentPickerComponent,
  ],
  templateUrl: './send-centralisation.component.html',
  styleUrls: ['./send-centralisation.component.css'],
})
export class SendCentralisationComponent extends BaseStepperAction {
  @ViewChild('actionModal') modal: ActionModalComponent;

  submitted = false;

  /** TinyMCE lifecycle gate; see `ComposeModalBase.modalOpen`. */
  protected modalOpen = false;

  protected renderLoading = false;

  /** One per mail the send will dispatch; `bodies` holds its prose at the same index. */
  protected mails: CompanyMailRecipient[] = [];

  protected activeMailIndex = 0;

  protected readonly SendMode = CentralisationSendMode;

  protected readonly ContractType = ContractType;

  protected readonly DocumentType = DocumentType;

  protected readonly editorPlugins = EDITOR_PLUGINS;

  protected readonly editorToolbar = EDITOR_TOOLBAR;

  protected readonly loadingText = $localize`Chargement…`;

  protected readonly form = new FormGroup({
    sendMode: new FormControl(CentralisationSendMode.PLATFORM, {
      nonNullable: true,
    }),
    lsa45Document: new FormControl<SignedContractDocument | null>(null),
    managementAgreementDocument: new FormControl<SignedContractDocument | null>(
      null,
      Validators.required
    ),
    identityCard: new FormControl<DocumentSummary | null>(null),
    signatureCertificate: new FormControl<DocumentSummary | null>(null),
    bodies: new FormArray<FormControl<string>>([]),
    /** The customer's stored documents added to every mail. */
    attachments: new FormControl<DocumentSummary[]>([], { nonNullable: true }),
  });

  get enabled(): boolean {
    return (
      this.state?.insuranceFlowData?.status ===
      InsuranceFlowStatus.PENDING_CENTRALISATION
    );
  }

  protected get familyMemberId(): string | null {
    return this.state?.insuranceFlowData?.memberId ?? null;
  }

  protected get userId(): string | null {
    return this.state?.insuranceFlowData?.userId ?? null;
  }

  constructor(
    taskDetailsService: TaskDetailsService,
    private readonly insuranceFlowService: InsuranceFlowService,
    private readonly toastrService: ToastrService
  ) {
    super(taskDetailsService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.subscription.add(
      this.form.controls.sendMode.valueChanges.subscribe((mode) => {
        this.applySendModeValidators(mode);
      })
    );
  }

  protected get isPlatformSend(): boolean {
    return (
      this.form.controls.sendMode.value === CentralisationSendMode.PLATFORM
    );
  }

  protected get submitDisabled(): boolean {
    return (
      this.form.invalid ||
      this.submitted ||
      (this.isPlatformSend && (this.renderLoading || this.mails.length === 0))
    );
  }

  /**
   * One tab per mail; the strip only shows when there is more than one.
   * Rebuilt with `mails`, never per change detection: a fresh array every pass
   * would make the strip re-render its buttons on each editor keystroke.
   */
  protected mailTabs: FilterTab[] = [];

  private buildMailTabs(): FilterTab[] {
    const labels = this.mails.map((recipient) =>
      getInsuranceTypeLabel(recipient.insuranceType)
    );
    return this.mails.map((recipient, index) => ({
      id: String(index),
      // Two companies can share a branch; the address then tells them apart.
      label:
        labels.filter((label) => label === labels[index]).length > 1
          ? `${labels[index]} — ${recipient.email}`
          : labels[index],
    }));
  }

  protected get activeRecipientEmail(): string {
    return this.mails[this.activeMailIndex]?.email ?? '';
  }

  protected get activeBodyControl(): FormControl<string> | null {
    return this.form.controls.bodies.at(this.activeMailIndex) ?? null;
  }

  protected setActiveMail(id: string): void {
    this.activeMailIndex = Number(id);
  }

  protected openModal(): void {
    if (this.submitted) return;
    this.resetForm();
    this.modal.openModal();
    this.modalOpen = true;
    this.fetchRender();
  }

  /** Whatever dismissed the modal, the editors must not outlive it. */
  protected onClosed(): void {
    this.modalOpen = false;
    this.renderLoading = false;
  }

  sendCentralisation() {
    const flowId = this.state?.insuranceFlowData?.id;
    if (this.submitted || !flowId) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const {
      sendMode,
      lsa45Document,
      managementAgreementDocument,
      identityCard,
      signatureCertificate,
      bodies,
      attachments,
    } = this.form.getRawValue();
    const skipEmail = sendMode === CentralisationSendMode.OFF_PLATFORM;
    this.submitted = true;
    this.insuranceFlowService
      .sendCentralisationFlow({
        flowId,
        skipEmail,
        lsa45DocumentId: lsa45Document?.documentId ?? null,
        managementAgreementDocumentId:
          managementAgreementDocument?.documentId ?? null,
        identityCardDocumentId: identityCard?.id ?? null,
        signatureCertificateDocumentId: signatureCertificate?.id ?? null,
        ...(skipEmail
          ? {}
          : {
              bodies: this.mails.map((recipient, index) => ({
                companyId: recipient.companyId,
                insuranceType: recipient.insuranceType,
                body: bodies[index],
              })),
            }),
        ...(attachments.length
          ? {
              attachmentDocumentIds: attachments.map((document) => document.id),
            }
          : {}),
      })
      .subscribe({
        next: (res) => {
          if (res.error) {
            this.displayError(res);
          } else {
            this.toastrService.success(
              skipEmail
                ? $localize`Centralisation marquée comme envoyée`
                : $localize`Centralisation envoyée à l'assureur`
            );
            this.modal?.closeModal();
            this.resetForm();
            this.refreshTaskDetails();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.displayError(err);
        },
      });
  }

  /** Prefills one editor per mail with what the send would dispatch. */
  private fetchRender(): void {
    const flowId = this.state?.insuranceFlowData?.id;
    if (!flowId) return;

    this.renderLoading = true;
    this.subscription.add(
      this.insuranceFlowService.renderCentralisation(flowId).subscribe({
        next: (res) => {
          // A slow answer must not repopulate the next opening.
          if (!this.modalOpen) return;
          this.renderLoading = false;
          if (res.error || !res.data) {
            this.displayRenderError(res);
          } else {
            this.setMails(res.data.mails ?? []);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (!this.modalOpen) return;
          this.renderLoading = false;
          this.displayRenderError(err);
        },
      })
    );
  }

  private setMails(mails: RenderCentralisationMailsResponse['mails']): void {
    const { bodies } = this.form.controls;
    bodies.clear();
    this.mails = mails.map((mail) => mail.recipient);
    this.mailTabs = this.buildMailTabs();
    this.activeMailIndex = 0;
    mails.forEach((mail) =>
      bodies.push(new FormControl(mail.body, { nonNullable: true }))
    );
    this.applySendModeValidators(this.form.controls.sendMode.value);
  }

  private displayError(
    source: APIResponse<CentralisationFlowResponse> | HttpErrorResponse
  ): void {
    this.toastrService.error(
      getApiErrorMessage(
        source,
        $localize`Une erreur est survenue. Veuillez réessayer ultérieurement`
      )
    );
    this.submitted = false;
  }

  private displayRenderError(
    source: APIResponse<RenderCentralisationMailsResponse> | HttpErrorResponse
  ): void {
    this.toastrService.error(
      getApiErrorMessage(
        source,
        $localize`Une erreur est survenue lors du chargement des emails`
      )
    );
  }

  protected resetForm(): void {
    this.form.controls.bodies.clear();
    this.mails = [];
    this.mailTabs = [];
    this.activeMailIndex = 0;
    this.form.reset({ sendMode: CentralisationSendMode.PLATFORM });
    this.applySendModeValidators(CentralisationSendMode.PLATFORM);
    this.submitted = false;
    this.renderLoading = false;
  }

  private applySendModeValidators(mode: CentralisationSendMode): void {
    // Only the management agreement is mandatory for a mailed send; the art. 45
    // declaration and the identity card ride along when picked.
    const managementAgreement = this.form.controls.managementAgreementDocument;
    if (mode === CentralisationSendMode.PLATFORM) {
      managementAgreement.setValidators(Validators.required);
    } else {
      managementAgreement.clearValidators();
    }
    managementAgreement.updateValueAndValidity();

    if (mode !== CentralisationSendMode.PLATFORM) {
      // The hidden pickers empty out so a hidden pick is never sent along.
      this.form.controls.lsa45Document.reset(null);
      this.form.controls.managementAgreementDocument.reset(null);
      this.form.controls.identityCard.reset(null);
      this.form.controls.signatureCertificate.reset(null);
      this.form.controls.attachments.reset();
    }

    // The prose survives a mode switch in case the operator comes back; only
    // the requirement moves.
    this.form.controls.bodies.controls.forEach((control) => {
      if (mode === CentralisationSendMode.PLATFORM) {
        control.setValidators(richTextRequired);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity();
    });
  }
}
